import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiText,
  EuiIcon,
  EuiPageTemplate,
} from "@inscopix/ideas-eui";
import ProjectIcon from "components/ProjectIcon/ProjectIcon";
import { ProjectTabAccess } from "components/ProjectTabs/ProjectTabAccess";
import { ProjectTabFiles } from "components/ProjectTabs/ProjectTabFiles";
import { ProjectTabProject } from "components/ProjectTabs/ProjectTabProject/ProjectTabProject";
import { ProjectTabTasks } from "components/ProjectTabs/ProjectTabTasks";
import { ProjectTabSettings } from "components/ProjectTabs/ProjectTabSettings";
import { RoleBadge } from "components/RoleBadge/RoleBadge";
import { Tenant, Project } from "graphql/_Types";
import { useProjectDataContext } from "pages/project/ProjectDataProvider";
import { useProjectPermissionStore } from "pages/project/ProjectPermissionProvider";
import {
  DynamicRoute,
  useRouteMapContext,
} from "providers/RouteMapProvider/RouteMapProvider";
import { useTenantContext } from "providers/TenantProvider/TenantProvider";
import {
  useSetCurrentPage,
  useTourStep,
} from "providers/TourProvider/TourProvider.hooks";
import { memo, useMemo } from "react";
import { Route, Switch } from "react-router-dom";
import { useLocation } from "react-router-dom-v5-compat";
import { ButtonIconCloneProject } from "components/ButtonIconCloneProject/ButtonIconCloneProject";
import { ButtonIconDeleteProject } from "components/ButtonIconDeleteProject/ButtonIconDeleteProject";
import { ProjectPageContent } from "./layout/ProjectPageContent";

export type ProjectTabName =
  | "Project"
  | "Tasks"
  | "Files"
  | "Access"
  | "Settings";

interface ProjectTab {
  name: ProjectTabName;
  icon: string;
  component: typeof ProjectTabAccess;
  route: DynamicRoute<
    string,
    {
      tenantKey: Tenant["key"];
      projectKey: Project["key"];
    },
    unknown
  >;
  id: string;
}

/*
 * Project home page with tab layout
 */
export const PageProjectTabs = memo(function PageProjectTabs() {
  const { project } = useProjectDataContext();
  const currentTenant = useTenantContext((s) => s.currentTenant);
  const permissions = useProjectPermissionStore((s) => s.permissions);
  const pathParams = {
    tenantKey: currentTenant.key,
    projectKey: project.key,
  };
  const role = useProjectPermissionStore((s) => s.permissions.role_display);
  const routeMap = useRouteMapContext((s) => s.routeMap);

  useSetCurrentPage("projectHome");

  const { component: TourProjectSharing0, anchorId } = useTourStep(
    "TourProjectSharing",
    0,
    () => {
      routeMap.PROJECT_ACCESS.dynamicPath(pathParams).navigateToReplace();
    },
  );
  const { component: TourProjectSharingPS0 } = useTourStep(
    "TourProjectSharingPS",
    0,
    () => {
      routeMap.PROJECT_ACCESS.dynamicPath(pathParams).navigateToReplace();
    },
  );

  const projectTabs: ProjectTab[] = useMemo(
    () => [
      {
        name: "Project" as const,
        icon: "tableDensityNormal",
        component: ProjectTabProject,
        route: routeMap.PROJECT,
        id: "projectTab",
      },
      {
        name: "Tasks" as const,
        icon: "compute",
        component: ProjectTabTasks,
        route: routeMap.PROJECT_TASKS,
        id: "projectTasks",
      },
      {
        name: "Files" as const,
        icon: "documents",
        component: ProjectTabFiles,
        route: routeMap.PROJECT_FILES,
        id: "projectFiles",
      },
      {
        name: "Access" as const,
        icon: "users",
        component: ProjectTabAccess,
        route: routeMap.PROJECT_ACCESS,
        id: anchorId ?? "projectAccess",
      },
      {
        name: "Settings" as const,
        icon: "gear",
        component: ProjectTabSettings,
        route: routeMap.PROJECT_SETTINGS,
        id: "projectSettings",
      },
    ],
    [anchorId, routeMap],
  );

  const location = useLocation();

  const tabOptions = projectTabs.map((tab) => {
    const dynamicPath = tab.route.dynamicPath(pathParams);
    return {
      label: tab.name,
      isSelected: location.pathname === dynamicPath.path,
      prepend: <EuiIcon type={tab.icon} />,
      onClick: () => {
        dynamicPath.navigateToReplace();
      },
      id: tab.id,
    };
  });

  const tabContent = useMemo(
    () =>
      projectTabs.map(({ id, route, component }) => (
        <Route
          key={id}
          path={route.parameterizedPath}
          exact
          component={component}
        />
      )),
    [projectTabs],
  );

  const projectActions = [
    <ButtonIconCloneProject
      key={"clone-project-action"}
      projectId={project.id}
    />,
  ];
  if (permissions.grant_access) {
    // unshift because EUI renders right side items in reverse order
    projectActions.unshift(
      <ButtonIconDeleteProject
        key="delete-project-action"
        projectId={project.id}
        tenantKey={currentTenant.key}
      />,
    );
  }

  return (
    <EuiPageTemplate responsive={[]}>
      <ProjectPageContent>
        <EuiPageTemplate.Header
          pageTitle={
            <EuiFlexGroup
              gutterSize={"s"}
              alignItems="center"
              responsive={false}
            >
              <EuiFlexItem grow={false}>
                <ProjectIcon
                  size="l"
                  project={project}
                  tenantKey={currentTenant.key}
                />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiText>
                  <h2>{project.name}</h2>
                </EuiText>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <RoleBadge role={role} />
              </EuiFlexItem>
            </EuiFlexGroup>
          }
          tabs={tabOptions}
          rightSideItems={projectActions}
          rightSideGroupProps={{
            gutterSize: "s",
          }}
        />
        <EuiPageTemplate.Section contentProps={{ style: { flexGrow: 1 } }}>
          <Switch>{tabContent}</Switch>
        </EuiPageTemplate.Section>
        {TourProjectSharing0}
        {TourProjectSharingPS0}
      </ProjectPageContent>
    </EuiPageTemplate>
  );
});
